import Decimal from "decimal.js";
import {
  OPPORTUNITY_STANDARD_REQUIRED_FIELD_LABELS,
  isBlankCustomFieldValue,
  type CustomFieldsSchema,
  type CustomFieldValues,
  type OpportunityStageRequiredFields,
  type OpportunityStandardRequiredField,
} from "@forgepoint/shared";

const FOCUSABLE_CONTROL_SELECTOR = "input, textarea, [role=combobox], button";

/** The opportunity values a stage's required fields are checked against. */
export interface StageRequirementValues {
  companyId: string | null;
  salesRepUserId: string | null;
  estimatorUserIds: string[];
  amount: string;
  locationId: string | null;
  customFields: CustomFieldValues;
}

/** One field a stage requires, with whether the opportunity has it filled. */
export type StageRequiredField =
  | {
      kind: "standard";
      field: OpportunityStandardRequiredField;
      label: string;
      isFilled: boolean;
    }
  | { kind: "custom"; name: string; label: string; isFilled: boolean };

/**
 * Lists a stage's required fields that still exist, standard first, then custom.
 * Custom names no longer in the schema are dropped, matching the server, which ignores them.
 * @param requiredFields - The stage's required standard and custom fields
 * @param values - The opportunity values to check
 * @param customFieldsSchema - The org's opportunity custom fields schema, if any
 * @returns Each required field with its label and whether it is filled
 */
export function getStageRequiredFields(
  requiredFields: OpportunityStageRequiredFields,
  values: StageRequirementValues,
  customFieldsSchema: CustomFieldsSchema | null | undefined,
): StageRequiredField[] {
  const standard: StageRequiredField[] = requiredFields.standard.map(
    (field) => ({
      kind: "standard",
      field,
      label: OPPORTUNITY_STANDARD_REQUIRED_FIELD_LABELS[field],
      isFilled: !isBlankStandardField(field, values),
    }),
  );
  const custom: StageRequiredField[] = getLiveCustomRequiredNames(
    requiredFields,
    customFieldsSchema,
  ).map((name) => ({
    kind: "custom",
    name,
    label: name,
    isFilled: !isBlankCustomFieldValue(values.customFields[name]),
  }));
  return [...standard, ...custom];
}

/**
 * Lists the labels of a stage's required fields that still exist, standard first.
 * @param requiredFields - The stage's required standard and custom fields
 * @param customFieldsSchema - The org's opportunity custom fields schema, if any
 * @returns The field labels
 */
export function getStageRequiredLabels(
  requiredFields: OpportunityStageRequiredFields,
  customFieldsSchema: CustomFieldsSchema | null | undefined,
): string[] {
  return [
    ...requiredFields.standard.map(
      (field) => OPPORTUNITY_STANDARD_REQUIRED_FIELD_LABELS[field],
    ),
    ...getLiveCustomRequiredNames(requiredFields, customFieldsSchema),
  ];
}

/**
 * Joins field names into a sentence list without an Oxford comma, e.g. "A, B and C".
 * @param names - The field names
 * @returns The joined names
 */
export function joinFieldNames(names: string[]): string {
  if (names.length <= 1) {
    return names.join("");
  }
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * Marks a field placeholder optional unless the selected stage requires the field.
 * @param placeholder - The placeholder without an optional suffix
 * @param isRequired - Whether the selected stage requires the field
 * @returns The placeholder to show on the field
 */
export function getFieldPlaceholder(
  placeholder: string,
  isRequired: boolean,
): string {
  if (isRequired) {
    return placeholder;
  }
  return `${placeholder} (optional)`;
}

/**
 * Checks whether a standard field is blank. An amount of zero counts as blank.
 * @param field - The standard field to check
 * @param values - The opportunity values
 * @returns True when the field has no meaningful value
 */
function isBlankStandardField(
  field: OpportunityStandardRequiredField,
  values: StageRequirementValues,
): boolean {
  if (field === "estimatorUserIds") {
    return values.estimatorUserIds.length === 0;
  }
  if (field === "amount") {
    return isBlankAmount(values.amount);
  }
  return !values[field];
}

/**
 * Checks whether an amount is blank. Zero and values still being typed, like ".", count as blank.
 * @param amount - The amount as typed
 * @returns True when the amount is not a usable non-zero number
 */
function isBlankAmount(amount: string): boolean {
  try {
    return new Decimal(amount).isZero();
  } catch {
    return true;
  }
}

/**
 * Keeps the required custom names that are still in the schema.
 * @param requiredFields - The stage's required fields
 * @param customFieldsSchema - The org's opportunity custom fields schema, if any
 * @returns The custom names that still exist
 */
function getLiveCustomRequiredNames(
  requiredFields: OpportunityStageRequiredFields,
  customFieldsSchema: CustomFieldsSchema | null | undefined,
): string[] {
  const knownFields = customFieldsSchema?.properties ?? {};
  return requiredFields.custom.filter((name) => name in knownFields);
}

/**
 * Moves keyboard focus to the control of a required field, if it is on screen.
 * Standard fields are found by `data-stage-field`, custom fields by `data-custom-field`.
 * @param field - The required field to focus
 * @returns Nothing
 */
export function focusStageField(field: StageRequiredField): void {
  document
    .querySelector(getStageFieldSelector(field))
    ?.querySelector<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR)
    ?.focus();
}

/**
 * Builds the selector of the element wrapping a required field's control.
 * @param field - The required field
 * @returns The CSS selector
 */
function getStageFieldSelector(field: StageRequiredField): string {
  if (field.kind === "standard") {
    return `[data-stage-field="${field.field}"]`;
  }
  return `[data-custom-field="${CSS.escape(field.name)}"]`;
}

/**
 * Names the editor's Save button, saying where the opportunity goes when saving moves it.
 * @param savedStageId - The stage the opportunity is saved in
 * @param selectedStage - The stage selected on the form, if any
 * @param missingCount - How many of the selected stage's required fields are blank
 * @returns "Save and move to <Stage>" when the stage changed and nothing is missing, otherwise "Save"
 */
export function getStageSaveLabel(
  savedStageId: string | null,
  selectedStage: { id: string; name: string } | undefined,
  missingCount: number,
): string {
  if (!selectedStage || selectedStage.id === savedStageId) {
    return "Save";
  }
  if (missingCount > 0) {
    return "Save";
  }
  return `Save and move to ${selectedStage.name}`;
}
